import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'next/router';
import _ from 'lodash';

import PageBase from '../../../../components/layout/PageBase';
import NotFound from '../../../../components/utility/NotFound';
import Listing from '../../../../components/pages/listing';
import ToriiLoader from '../../../../components/utility/ToriiLoader';
import { fetchListingById } from '../../../../actions/listing';
import { fetchShowings } from '../../../../actions/showing';
import { fetchFavorites } from '../../../../actions/favorite';
import fetchApi from '../../../../services/api';
import { addressString, formatAsDollars } from '../../../../lib/helpers';
import * as types from '../../../../actions/actionTypes';
import style from '../../../../styles/sass/pages/ListingPage.module.scss';
import { isMobile } from '../../../../lib/getDeviceSize';

const styles = {
  container: {
    paddingLeft: 0,
    paddingRight: 0,
  },
};

export interface Props {
  dispatch: any;
  fetchFavorites: Function;
  fetchListingById: Function;
  fetchShowings: Function;
  initialListing: any;
  listingFetched: boolean;
  listingLoading: boolean;
  listing: any;
  router: any;
  token: string;
}

export interface State {
  initialShowingOpen: boolean;
  listing: any;
  price: number;
}

export class ListingPage extends React.Component<Props, State> {
  // Since the head tag's values are defined at initial load of the page
  // we get a listing details before loading the page,
  // so that we can pass them into meta and open graph tags
  static async getInitialProps(args: any) {
    let host = '';
    if (args && args.req && args.req.headers && args.req.headers.host) {
      host = args.req.headers.host;
    }
    const response = await fetchApi(
      `/listing/id/f/${args.query.listingId}`,
      undefined,
      'get',
      undefined,
      undefined,
      host,
    );
    const json = await response.json();
    const { listing } = json;
    return { initialListing: listing };
  }

  constructor(props: Props) {
    super(props);
    const il = this.props.initialListing;
    let price: number = 0;
    if (il) {
      price = il.salePrice ? il.salePrice : il.listPrice;
    }
    this.state = {
      initialShowingOpen: false,
      listing: il,
      price,
    };
  }

  initialDataFetch() {
    const { photosByRoom } = this.props.router.query;
    this.props.fetchListingById(
      this.props.router.query.listingId,
      photosByRoom,
    );
  }

  componentDidMount() {
    // If there is no initialListing then we are redirecting to 404 page
    if (!this.props.initialListing) {
      this.props.router.push('/404');
    }

    this.initialDataFetch();
    if (this.props.token) {
      this.props.fetchFavorites(this.props.token);
      this.props.fetchShowings(this.props.token);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!_.isEqual(this.props.listing, prevProps.listing)) {
      this.setState({
        listing: this.props.listing,
      });
    }

    if (
      this.props.router.query.listingId !== prevProps.router.query.listingId
    ) {
      this.initialDataFetch();
    }

    // If the user came in with "showing=true", open the showing request dialog
    if (
      this.props.router.query.showing &&
      this.props.router.query.showing === 'true' &&
      Object.keys(this.state.listing).length > 0 &&
      this.state.initialShowingOpen === false
    ) {
      this.setState(
        {
          initialShowingOpen: true,
        },
        () => {
          this.props.dispatch({
            listing: this.state.listing,
            type: types.OpenScheduleShowingDialog,
          });
        },
      );
    }
  }

  render() {
    let pageTitle = 'Torii - The easiest way to buy a home';
    if (this.state.listing && this.state.price) {
      pageTitle = `${addressString(this.state.listing)} - ${formatAsDollars(
        this.state.price,
      )} - Torii 🏡`;
    }
    if (
      this.props.listingFetched &&
      this.state.listing &&
      Object.keys(this.state.listing).length !== 0
    ) {
      return (
        <PageBase
          hideFooter={isMobile()}
          key={this.state.listing.id}
          listing={this.state.listing}
          title={pageTitle}
        >
          <div
            className={`page-container ${style.listingContainer} listing-container col-xs-12 row center-xs`}
          >
            <div className="col-xs-12" style={styles.container}>
              <Listing />
            </div>
          </div>
        </PageBase>
      );
    } else if (
      !this.state.listing &&
      !this.props.listingLoading &&
      this.props.listingFetched &&
      Object.keys(this.state.listing || {}).length === 0
    ) {
      return <NotFound />;
    } else {
      return (
        <PageBase listing={this.state.listing} title={pageTitle}>
          <ToriiLoader bottom={150} top={150} />
        </PageBase>
      );
    }
  }
}

const mapStateToProps = (state: any) => ({
  listing: state.listings.listing,
  listingFetched: state.listings.listingFetched,
  listingLoading: state.listings.listingLoading,
  token: state.auth.token,
});

const mapDispatchToProps = (dispatch: any, ownProps: any) => ({
  dispatch,
  fetchFavorites(token: string) {
    dispatch(fetchFavorites(token));
  },
  fetchListingById(listingId: number, photosByRoom: string | undefined) {
    dispatch(fetchListingById(listingId, true, photosByRoom, false));
  },
  fetchShowings(token: string) {
    dispatch(fetchShowings(token));
  },
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ListingPage),
);
